/**
 * Main menu styling.
 *
 * One stylesheet, injected with the menu and removed with it. Sizes are fluid rather than
 * per-breakpoint: `clamp` on type and `auto-fit` on the card grid mean the same layout serves a
 * phone in portrait and a desktop, and the media queries below only handle the two cases fluid
 * sizing cannot, a landscape phone with no vertical room and a request for less motion.
 *
 * The palette is the HUD palette: bone white on near black, amber for anything live.
 *
 * The root is fixed to the viewport and scrolls; the page inside it is what carries the width, so
 * every panel lines up on one measure instead of the name row spanning a desktop while the mode
 * grid stops at 1180px.
 *
 * Every control on this screen that is not a mode card is the same button. The name row, the save
 * panel and the join row each ship their own stylesheet, because each of them mounts on screens
 * this file knows nothing about, and left alone that produced three different buttons in one
 * column. The band below reaches into all three and states the shape once. It is the menu saying
 * what its own chrome looks like, not a panel being redefined behind its owner's back: outside the
 * menu those same panels keep every rule they declared.
 */

import { injectCss } from '@/ui/cssInjector';

const CSS = `
.menu {
  position: fixed;
  inset: 0;
  z-index: 10;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding:
    calc(env(safe-area-inset-top, 0px) + clamp(24px, 6vh, 64px))
    calc(env(safe-area-inset-right, 0px) + clamp(20px, 5vw, 72px))
    calc(env(safe-area-inset-bottom, 0px) + clamp(24px, 5vh, 56px))
    calc(env(safe-area-inset-left, 0px) + clamp(20px, 5vw, 72px));
  font: 500 16px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: #f2ede3;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.menu-page {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: clamp(20px, 4vh, 38px);
  width: 100%;
  max-width: 1180px;
  margin-inline: auto;
}

/*
 * A title and the box under it, held together. The page gap separates one group from the next, and
 * this one, much smaller, is what makes a title read as belonging to the box below rather than
 * floating equidistant between two of them.
 */
.menu-group {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

/*
 * Sits over whatever the backdrop is drawing, and reads the same when there is no backdrop. The
 * scrim is heaviest on the left, under the text, and thins out to the right, where the backdrop
 * camera puts the plate rack.
 */
.menu::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(90% 70% at 14% 12%, rgba(232, 179, 74, 0.1), transparent 60%),
    linear-gradient(
      100deg,
      rgba(8, 9, 11, 0.88) 0%,
      rgba(8, 9, 11, 0.74) 34%,
      rgba(8, 9, 11, 0.28) 72%,
      rgba(8, 9, 11, 0.18) 100%
    ),
    linear-gradient(180deg, rgba(8, 9, 11, 0.4) 0%, transparent 35%, rgba(8, 9, 11, 0.62) 100%);
}

/*
 * The screen while a mode is being fetched. The inert attribute already stops every press and
 * every tab stop, so this is only what says so. Dimmed card by card rather than by dropping the
 * opacity of the whole menu, because opacity on the root would take the card that is actually
 * starting down with it, and that is the one thing here still doing something.
 */
.menu[inert] { cursor: default; }
.menu[inert] .mode-card:not(.is-starting),
.menu[inert] .menu-wordmark,
.menu[inert] .menu-join { opacity: 0.5; }

/*
 * Deliberately short of a splash screen. The mast has to still be a header once the grid below it
 * holds a dozen modes, so it takes a fixed slice of the viewport and no more.
 */
.menu-wordmark {
  margin: 0;
  font-size: clamp(38px, 7vw, 68px);
  font-weight: 700;
  line-height: 0.92;
  letter-spacing: clamp(4px, 1.2vw, 12px);
  text-shadow: 0 6px 30px rgba(0, 0, 0, 0.75);
}


/*
 * Who you are, the character behind the name, and the file it lives in: one card, because they are
 * one subject. The pieces inside it are built by three different files and framed by none of them.
 */
/*
 * The two panels the menu frames itself: who you are, and the way in that names no mode. Stated
 * together because they have to look like one decision made twice, and because a bare button
 * floating between two framed things is the thing this screen was rebuilt to stop having.
 *
 * Border box, alone on this screen, because nothing in this repo sets a global one. A full-width
 * card with padding is the one shape that needs it: on a phone the padding lands outside the
 * viewport and the whole menu gains a horizontal scrollbar.
 */
.menu-band, .menu-join {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 680px;
  padding: 12px 14px;
  border: 1px solid rgba(242, 237, 227, 0.14);
  border-radius: 8px;
  background: rgba(8, 9, 11, 0.45);
}

.menu-band .name-roller,
.menu-band .save-transfer {
  width: auto;
  max-width: none;
  padding: 0;
  border: 0;
  background: none;
}

/*
 * The four things you can do to a character, on one line: reroll the name, spend points, write the
 * file out, read one back. It wraps on a phone, and the box is sized so that it does not before it
 * has to.
 */
.menu-band-actions { display: flex; align-items: flex-start; gap: 10px; flex-wrap: wrap; }

/* The name above them reads, so it does not need the row's alignment or the row's width. */
.menu-band .name-roller { gap: 10px; }

/*
 * The row at the foot of the page: what the game does rather than what a character is, which is why
 * it sits below the modes instead of in the band with the name.
 */
.menu-utility { align-self: stretch; display: flex; gap: 10px; flex-wrap: wrap; }

/*
 * The one button on this screen that is not a mode card, stated once and worn by all five of them.
 * Quiet on purpose: none of these is the reason anyone opened the game, and a second lit control
 * would compete with the card that starts one.
 */
.menu-character,
.menu-settings,
.menu-join-go,
.menu-band .name-roller-dice,
.menu-band .save-transfer-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid rgba(242, 237, 227, 0.24);
  border-radius: 6px;
  background: rgba(242, 237, 227, 0.05);
  color: rgba(242, 237, 227, 0.82);
  font: inherit;
  font-size: clamp(11px, 3vw, 12.5px);
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease;
}

.menu-character:hover,
.menu-character:focus-visible,
.menu-settings:hover,
.menu-settings:focus-visible,
.menu-join-go:hover,
.menu-join-go:focus-visible,
.menu-band .name-roller-dice:hover,
.menu-band .name-roller-dice:focus-visible,
.menu-band .save-transfer-button:hover,
.menu-band .save-transfer-button:focus-visible {
  border-color: rgba(242, 237, 227, 0.45);
  background: rgba(242, 237, 227, 0.12);
  color: #f2ede3;
}

.menu-character:focus-visible,
.menu-settings:focus-visible,
.menu-join-go:focus-visible,
.menu-band .name-roller-dice:focus-visible,
.menu-band .save-transfer-button:focus-visible {
  outline: 2px solid #e8b34a;
  outline-offset: 2px;
}

/* The confirm is the exception, and it is the only one: it is the press that overwrites a save. */
.menu-band .save-transfer-confirm {
  border-color: rgba(232, 179, 74, 0.55);
  background: rgba(232, 179, 74, 0.12);
  color: #e8b34a;
}

.menu-band .save-transfer-confirm:hover, .menu-band .save-transfer-confirm:focus-visible {
  border-color: #e8b34a;
  background: rgba(232, 179, 74, 0.24);
  color: #e8b34a;
}

.menu-section {
  align-self: stretch;
  display: flex;
  align-items: baseline;
  width: 100%;
  max-width: 680px;
  gap: 14px;
  font-size: clamp(10px, 2.6vw, 12px);
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(242, 237, 227, 0.55);
}

.menu-section::after {
  content: "";
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.22), transparent);
}

/*
 * auto-fill, not auto-fit: a single mode gets one card of a sensible width rather than one card
 * stretched across a desktop, and ten modes fill the row. The max-width caps the grid at four
 * columns on a wide screen, past which cards get too far apart to scan.
 */
.menu-modes {
  align-self: stretch;
  display: grid;
  gap: clamp(10px, 1.8vw, 16px);
  align-self: stretch;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
  margin: 0;
  padding: 0;
  list-style: none;
}

/*
 * The item is a flex box so the card can stretch to the row without asking for a percentage height.
 * A percentage against a track that is sizing itself off the same card is a cycle, and the
 * browser resolves it by sizing the row to nothing and letting the card spill out of it, which is
 * how the card came to overlap the block below it.
 */
.menu-modes > li { display: flex; }

/*
 * Every card is the same height in its row, and its two prose lines are clamped, so a mode with a
 * long pitch cannot set the height of the whole grid.
 */
.mode-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  width: 100%;
  min-height: 44px;
  padding: clamp(16px, 3.4vw, 20px);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  background: linear-gradient(160deg, rgba(22, 24, 28, 0.82), rgba(12, 13, 15, 0.86));
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease, transform 160ms ease, background 160ms ease;
}

/* Corner bracket: the only decoration, and it draws the eye to the live card. */
.mode-card::after {
  content: "";
  position: absolute;
  top: 12px;
  right: 12px;
  width: 16px;
  height: 16px;
  border-top: 2px solid rgba(232, 179, 74, 0.35);
  border-right: 2px solid rgba(232, 179, 74, 0.35);
  border-radius: 0 4px 0 0;
  transition: border-color 160ms ease;
}

.mode-card:hover,
.mode-card:focus-visible,
.mode-card:focus-within {
  border-color: rgba(232, 179, 74, 0.55);
  background: linear-gradient(160deg, rgba(30, 32, 37, 0.88), rgba(15, 16, 19, 0.9));
  transform: translateY(-2px);
  outline: none;
}

.mode-card:hover::after,
.mode-card:focus-within::after { border-color: #e8b34a; }

/*
 * The card that was pressed, while its mode is being fetched. Everything around it has gone down to
 * half, so this is what keeps one card lit: the same treatment hover gives, held rather than
 * following the pointer.
 */
.mode-card.is-starting { border-color: rgba(232, 179, 74, 0.55); }

.mode-card.is-starting::after { border-color: #e8b34a; }

.mode-title { font-size: clamp(18px, 4.2vw, 21px); font-weight: 700; letter-spacing: 1px; }
.mode-tagline { font-size: clamp(11px, 3vw, 12.5px); color: #e8b34a; }

.mode-facts { display: flex; flex-wrap: wrap; gap: 6px; margin: 2px 0 0; padding: 0; }

.mode-fact {
  display: flex;
  gap: 6px;
  padding: 4px 9px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  font-size: clamp(10px, 2.8vw, 12px);
  white-space: nowrap;
}

.mode-fact-label { text-transform: uppercase; letter-spacing: 1px; opacity: 0.5; }

/* Pinned to the bottom of the card so the row of them lines up however tall the prose ran. */
/*
 * The two ways into a mode, side by side. Playing alone is the loud one because it is the one that
 * needs nothing from anybody else, and hosting sits beside it rather than under it so neither reads
 * as the small print of the other.
 */
.mode-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: auto; padding-top: 10px; }

.mode-play, .mode-host {
  flex: 1 1 auto;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid rgba(232, 179, 74, 0.55);
  border-radius: 6px;
  background: rgba(232, 179, 74, 0.1);
  color: #e8b34a;
  font: inherit;
  font-size: clamp(11px, 3vw, 12.5px);
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease;
}

.mode-host {
  border-color: rgba(242, 237, 227, 0.24);
  background: rgba(242, 237, 227, 0.05);
  color: rgba(242, 237, 227, 0.82);
}

.mode-play:hover, .mode-play:focus-visible { background: rgba(232, 179, 74, 0.2); }
.mode-host:hover, .mode-host:focus-visible { background: rgba(242, 237, 227, 0.12); }
.mode-play:focus-visible,
.mode-host:focus-visible { outline: 2px solid #e8b34a; outline-offset: 2px; }

/* Joining names no mode, so it is its own box below the grid rather than a third card. */
.menu-join { margin: 0; }

.menu-join-row { display: flex; align-items: center; gap: 10px; }

.menu-join-input {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid rgba(242, 237, 227, 0.24);
  border-radius: 6px;
  background: rgba(8, 9, 11, 0.6);
  color: #f2ede3;
  font: inherit;
  font-size: 20px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.menu-join-input:focus-visible { outline: 2px solid #e8b34a; outline-offset: 2px; }

.menu-join-problem {
  margin: 10px 0 0;
  font-size: clamp(11px, 3vw, 12.5px);
  color: #e8b34a;
}

/* Why the last session ended. Loud, because arriving here with no explanation reads as a crash. */
.menu-ended {
  display: grid;
  gap: 4px;
  max-width: 62ch;
  margin: 0 0 clamp(10px, 2vh, 18px);
  padding: 14px 18px;
  border-left: 3px solid #e8b34a;
  border-radius: 0 6px 6px 0;
  background: rgba(232, 179, 74, 0.1);
}

.menu-ended-message { font-weight: 700; letter-spacing: 0.5px; }
.menu-ended-advice { font-size: clamp(11px, 3vw, 12.5px); color: rgba(242, 237, 227, 0.72); }

.menu .is-hidden { display: none; }


/* A phone held sideways has no vertical room to spend on a wordmark. */
@media (max-height: 560px) and (orientation: landscape) {
  .menu-page { gap: 14px; }
  .menu-wordmark { font-size: clamp(32px, 7vh, 52px); }
}

@media (prefers-reduced-motion: reduce) {
  .mode-card { transition: none; }
  .mode-card:hover, .mode-card:focus-visible { transform: none; }
}
`;

/** Injects the menu stylesheet. Returns a function that removes it again. */
export function injectMenuStyles(): () => void {
  return injectCss(CSS);
}
